import { useCallback } from 'react';

import { useMixpanelTracking } from '../../../tracking/mixpanel/use-mixpanel-tracking';
import { useJsaTrackingContext } from './use-jsa-tracking-context';

type ItemType = 'job-level' | 'job-role';

type TrackWorkflowTransitionParams = {
    readonly actionType: 'assign_reviewer' | 'publish' | 'review_completed' | 'unreview';
    readonly fromStatus?: string;
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly parentRoleId?: string;
    readonly targetUserId?: string;
    readonly toStatus?: string;
    readonly versionId: string;
};

type UseTrackWorkflowTransitionOptions = {
    readonly architectureId: string;
};

type UseTrackWorkflowTransitionResult = {
    readonly trackWorkflowTransition: (params: TrackWorkflowTransitionParams) => void;
};

const getLevelId = (itemType: ItemType, itemId: string): string | undefined =>
    itemType === 'job-level' ? itemId : undefined;

const getRoleId = (itemType: ItemType, itemId: string, parentRoleId?: string): string | undefined =>
    itemType === 'job-role' ? itemId : parentRoleId;

export const useTrackWorkflowTransition = (
    options: UseTrackWorkflowTransitionOptions,
): UseTrackWorkflowTransitionResult => {
    const { architectureId } = options;
    const { trackEvent } = useMixpanelTracking();
    const trackingContext = useJsaTrackingContext();

    const trackWorkflowTransition = useCallback(
        (params: TrackWorkflowTransitionParams): void => {
            trackEvent({
                name: 'job-role-workflow-transition',
                payload: {
                    actionType: params.actionType,
                    actorRoles: trackingContext.editorRoles,
                    actorUserId: trackingContext.userId,
                    architectureId,
                    fromStatus: params.fromStatus,
                    itemType: params.itemType,
                    levelId: getLevelId(params.itemType, params.itemId),
                    roleId: getRoleId(params.itemType, params.itemId, params.parentRoleId),
                    targetUserId: params.targetUserId,
                    tenantId: trackingContext.tenantId,
                    timestamp: trackingContext.timestamp,
                    toStatus: params.toStatus,
                    versionId: params.versionId,
                },
            });
        },
        [trackEvent, trackingContext, architectureId],
    );

    return { trackWorkflowTransition };
};
